import { Account, Right, SearchBold } from '@tencent/odc-iconpack';
import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';

import Empty from '../Empty';
import OdcInput from '../Input/odcInput';
import { useComponentLocale } from '../locale';

import './index.scss';

export type IGlobalSearchSuggestionItem = {
  id: string;
  label: string;
  url: string;
}

export type IGlobalSearchSuggestionGroup = {
  title: string;
  suggestions: IGlobalSearchSuggestionItem[];
}

export type IGlobalSearchResultItem = {
  id: string;
  label: string;
  url: string;
}

export type IGlobalSearchResultGroup = {
  title: string;
  results: IGlobalSearchResultItem[];
}

export type IGlobalSearch = {
  value: string;
  resultsCount?: number;
  placeholder?: string;
  type?: 'split' | 'panel';
  escTag?: boolean;
  suggestions?: IGlobalSearchSuggestionGroup[];
  results?: IGlobalSearchResultGroup[];
  onChange: (e: React.ChangeEvent<HTMLInputElement>, value: string) => void;
  onViewMore: () => void;
  style?: React.CSSProperties;
}

const GlobalSearch = (props: IGlobalSearch) => {
  const { t } = useComponentLocale();
  const { value, onChange, placeholder, suggestions = [], results = [], resultsCount, onViewMore, type = 'split', escTag = false, style } = props;
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const showEmpty = (value && results.length === 0) || suggestions.length === 0;
  const showResults = (value && results.length > 0);
  const showSuggestions = value.length === 0 && suggestions.length > 0;

  // 获取所有可选择的项目
  const getAllSelectableItems = useCallback(() => {
    const items: IGlobalSearchSuggestionItem[] = [];
    suggestions.forEach(group => {
      items.push(...group.suggestions);
    });
    return items;
  }, [suggestions]);

  // 当type为panel且显示suggestions时，默认选中第一个
  useEffect(() => {
    if (type === 'panel' && showSuggestions) {
      const allItems = getAllSelectableItems();
      setSelectedIndex(allItems.length > 0 ? 0 : -1);
    } else {
      setSelectedIndex(-1);
    }
  }, [type, showSuggestions, getAllSelectableItems]);

  // 滚动到选中的元素
  useEffect(() => {
    if (type === 'panel' && selectedIndex >= 0) {
      const selectedElement = document.getElementById(`suggestion-item-${selectedIndex}`);
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }
  }, [selectedIndex, type]);

  // 处理键盘事件
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (type !== 'panel' || !showSuggestions) return;

    const allItems = getAllSelectableItems();
    if (allItems.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % allItems.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev <= 0 ? allItems.length - 1 : prev - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < allItems.length) {
          const selectedItem = allItems[selectedIndex];
          window.open(selectedItem.url, '_blank');
        }
        break;
    }
  }, [type, showSuggestions, selectedIndex, getAllSelectableItems]);

  const escapeRegExp = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return (
    <div
      className={classNames(`odc-global-search odc-global-search-${type}`)}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className={classNames('odc-global-search-input-wrapper', {
        'with-esc': escTag,
      })}
      >
        <OdcInput
          value={value}
          search
          placeholder={placeholder}
          className="odc-global-search-input"
          onChange={onChange}
          autoFocus
        />
        {escTag && <div className="esc-tag">Esc</div>}
      </div>
      <div className={classNames('odc-global-dropdown-content', {
        'has-dropdown-animation': type === 'split',
      })}
      >
        {showSuggestions && <div className="suggestion-group">
          {suggestions.map(group => {
            // 计算当前组之前的所有项目数量
            const prevGroupsCount = suggestions.slice(0, suggestions.indexOf(group))
              .reduce((count, prevGroup) => count + prevGroup.suggestions.length, 0);

            return (
              <div key={group.title} className="suggestion-group-item">
                <div className="suggestion-title">{group.title}</div>
                <div className="suggestion-list">
                  {group.suggestions.map((item, itemIndex) => {
                    const globalIndex = prevGroupsCount + itemIndex;
                    const isSelected = type === 'panel' && selectedIndex === globalIndex;

                    return (
                      <div
                        id={`suggestion-item-${globalIndex}`}
                        key={item.id}
                        className={classNames('suggestion-item', {
                          'selected': isSelected,
                        })}
                        onClick={(e) => { e.preventDefault(); window.open(item.url, '_blank'); }}
                      >
                        {/* 如果是panel类型的，展示向右箭头 */}
                        {type === 'panel' && <Right className="!mr-1r text-base" />}
                        {item.label}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>}
        {showResults && <div className="search-group">
          <div className="search-group-list">
            {results.map(group => (
              <div key={group.title} className="search-group-item">
                <div className="search-title">{group.title}</div>
                <div className="search-list">
                  {group.results.map(item => (
                    <div
                      key={item.id}
                      className="search-item"
                      onClick={(e) => { e.preventDefault(); window.open(item.url, '_blank'); }}
                    >
                      <Account className="!mr-1r text-base" />
                      {item.label.split(new RegExp(`(${escapeRegExp(value)})`, 'gi')).map((part, index) => (
                        <span
                          key={`search_item_${item.id}_${index}`}
                          style={{ fontWeight: part.toLowerCase() === value.toLowerCase() ? 600 : 400 }}
                        >
                          {part}
                        </span>
                      ))}

                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="view-more-wrapper">
            <div className="view-more" onClick={onViewMore}>
              <SearchBold className="!mr-1r" />
              {t('GlobalSearch.seeAllResults', { query: value, count: resultsCount ?? 0 })}
            </div>
          </div>
        </div>}
        {showEmpty && <div className="search-empty">
          <Empty
            emptyTip={t('GlobalSearch.noQuickMatches')}
            emptyDesc={t('GlobalSearch.tryCheckingTypos')}
          />
        </div>}
      </div>
    </div>
  );
};

export default GlobalSearch;